<script setup lang="ts">
import {ref, computed, onMounted} from 'vue';
import {type Poll, type Post, type PollOption} from "~/repository/types.d";
import {useNow, useIntervalFn} from '@vueuse/core';

const props = defineProps<{
  post: Post
  poll: Poll
}>()

const poll = ref(props.poll);
const {user} = useAuthStore()
const {vote: pollVote} = usePollAPI(ref(props.post), poll)
const selectedOption = ref<PollOption | null>(props.poll.options.find(option => option.users_choice === true) ?? null);

const pollExpirationTime = computed(() => new Date(props.post.published_at).getTime() + 24 * 60 * 60 * 1000);
const currentTime = useNow();
const timeRemaining = computed(() => {
  const duration = pollExpirationTime.value - currentTime.value.getTime();
  const hours = String(Math.floor(duration / 3600000)).padStart(2, '0');
  const minutes = String(Math.floor((duration % 3600000) / 60000)).padStart(2, '0');
  const seconds = String(Math.floor((duration % 60000) / 1000)).padStart(2, '0');
  return `${hours}:${minutes}:${seconds}`;
});
const isPollActive = computed(() => pollExpirationTime.value > currentTime.value.getTime());

function getFillment(option: PollOption) {
  if (poll.value.total_votes === 0) {
    return '0%';
  }
  const percentage = (option.votes / poll.value.total_votes) * 100;
  return Number.isInteger(percentage) ? `${percentage}%` : `${percentage.toFixed(2)}%`;
}

async function vote(option: PollOption) {
  if (!poll.value.has_voted && isPollActive.value) {
    await pollVote(option)
    selectedOption.value = option;
  }
}

onMounted(() => {
  useIntervalFn(() => {
    currentTime.value = new Date();
  }, 1000);
});
</script>

<template>
  <p class="text-xl text-center w-full font-bold text-white">{{ poll.question }}</p>
  <div class="relative" v-for="(option, index) in poll.options" :key="index">
    <div
      :class="{
        'relative p-3 h-12 flex mt-2 justify-between overflow-hidden items-center select-none font-semibold rounded-xl text-sm bg-gray-950  placeholder:text-gray placeholder:font-semibold focus-visible:ring-0 focus-visible:ring-offset-0 focus:ring-0 focus:ring-offset-0 border-gray-700 border': true,
        'hover:cursor-pointer hover:bg-gray-800': !poll.has_voted && isPollActive
      }"
      @click="vote(option)"
    >
      <span :class="{
        'z-20 relative text-white': true,
        '!text-gray': poll.has_voted && selectedOption !== option
      }" style="mix-blend-mode: exclusion;">{{
          option.label
        }}</span>
      <div class="flex items-center gap-3" v-if="poll.has_voted || !isPollActive">
        <LazyUserAvatar v-if="selectedOption === option" class="w-5 h-5 relative z-20 border border-gray-50"
                        :user="user"/>
        <span :class="{
          'z-20 relative text-white text-xs': true,
          '!text-gray': selectedOption !== option
        }" style="mix-blend-mode: exclusion;">{{ getFillment(option) }}</span>
      </div>
      <div
        :class="{
          'z-10 h-full top-0 left-0 absolute w-0 rounded-lg origin-top-left': true,
          'bg-white': selectedOption === option,
          'bg-gray-950': selectedOption !== option
        }"
        :style="{
          transition: 'all 0.5s ease-out',
          width: (poll.has_voted || !isPollActive) ? `${(option.votes / poll.total_votes) * 100 }%` : null,
        }"
      ></div>
    </div>
  </div>
  <div class="text-gray mt-2 text-xs flex items-center">
    <span v-if="poll.total_votes > 0 || !isPollActive">{{ poll.total_votes }} votes</span>
    <template v-if="isPollActive">
      <span v-if="poll.total_votes > 0" class="mx-1">·</span><span>Zakończenie: {{
        timeRemaining
      }}</span>
    </template>
    <template v-else><span class="mx-1">·</span><span>Głosowanie zakończone</span></template>
  </div>
</template>
